import React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { isMobile } from 'react-device-detect'

import {
  PixelCurrency,
  PixelDate,
  PixelFlexBox,
  PixelTable,
  PixelText,
  PixelTooltip,
  DcTag,
  colors,
  DcFlexBox,
} from 'pixel-base'
import styled from 'styled-components'
import DIcon from 'utils/icons/d-icon'
import ExchangeIcon from 'utils/icons/exchange-icon'
import HistoryIcon from 'utils/icons/history-icon'
import SplitIcon from 'utils/icons/split-icon'
import TriangleExclamationIcon from 'utils/icons/triangle-exclamation-icon'
import WhatsAppIcon from 'utils/icons/whatsapp-icon'
import { IOrder } from 'client/../decl'
import DeliveryRiskCell from './deliveryRisk/DeliveryRiskCell'

interface Props {
  orders: IOrder[]
  tableLoading: boolean
  totalRows: number
  filters: any
  handlePerRowsChange: (perPage: number) => void
  handlePageChange: (page: number) => void
  onRowClicked: (row: any) => void
  handleChange: (selected: any) => void
  toggledClearRows: boolean
  selectedOrders: IOrder[]
  resetPaginationToggle: boolean
  subHeaderComponent: React.ReactNode
  isVisible: boolean
}
const HintContainer = styled.div`
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin: 0.25rem 0;
  font-size: 0.875rem;
  line-height: 1.25;
`

const ExpandedComponent: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.customer_notes) return null
  return (
    <HintContainer
      data-testid="expanded-row-content"
      role="region"
      aria-label="Customer Notes"
    >
      <strong style={{ fontSize: '0.875rem' }}>Customer Notes:</strong>{' '}
      {data.customer_notes}
    </HintContainer>
  )
}

const TruncatedCellText: React.FC<{ text?: string | null }> = ({ text }) => {
  const textRef = React.useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)
  const displayText = text?.trim() ? text : '--'

  const checkTruncation = React.useCallback(() => {
    const element = textRef.current
    if (!element) return
    const truncated = element.scrollWidth > element.clientWidth
    setIsTruncated((previous) =>
      previous === truncated ? previous : truncated,
    )
  }, [])

  React.useLayoutEffect(() => {
    checkTruncation()
  }, [checkTruncation, displayText])

  React.useEffect(() => {
    const element = textRef.current
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      checkTruncation()
    })
    observer.observe(element)
    if (element.parentElement) {
      observer.observe(element.parentElement)
    }

    return () => {
      observer.disconnect()
    }
  }, [checkTruncation, displayText])

  return (
    <TextClamp>
      <EllipsisText ref={textRef}>{displayText}</EllipsisText>
      {isTruncated && displayText !== '--' && (
        <PixelTooltip
          tooltipText={displayText}
          tooltipPlacement="top"
          width="100%"
          style={{
            position: 'absolute',
            inset: 0,
            minWidth: 0,
            maxWidth: '100%',
            height: '100%',
          }}
        >
          <TooltipHitTarget tabIndex={0} aria-label={displayText} />
        </PixelTooltip>
      )}
    </TextClamp>
  )
}

const OrdersTable: React.FC<Props> = ({
  orders,
  tableLoading,
  totalRows,
  filters,
  handlePerRowsChange,
  handlePageChange,
  onRowClicked,
  handleChange,
  toggledClearRows,
  selectedOrders,
  resetPaginationToggle,
  subHeaderComponent,
  isVisible,
}) => {
  type TrackingStatus = 'error' | 'timeout' | 'awaiting' | string | number
  const getTrackingObject = (
    tracking: TrackingStatus,
    errorMessage: string,
  ): React.ReactNode => {
    if (tracking === 'error') {
      return (
        <OverlayTrigger
          placement={'top'}
          overlay={
            <Tooltip id={'tooltip-error'}>
              {errorMessage ||
                'Recreate the consignment, and if the issue persists, contact the support team.'}
            </Tooltip>
          }
        >
          <PixelText>{tracking}</PixelText>
        </OverlayTrigger>
      )
    }
    if (tracking === 'timeout') {
      return (
        <OverlayTrigger
          placement={'top'}
          overlay={
            <Tooltip id={'tooltip-timeout'}>
              The courier didn&apos;t respond to our API request; the
              consignment may have been created on their portal.
            </Tooltip>
          }
        >
          <PixelText>{tracking}</PixelText>
        </OverlayTrigger>
      )
    }
    if (tracking === 'awaiting') {
      return (
        <OverlayTrigger
          placement={'top'}
          overlay={
            <Tooltip id={'tooltip-awaiting'}>
              Awaiting Tracking ID from SmartLane
            </Tooltip>
          }
        >
          <PixelText>{tracking}</PixelText>
        </OverlayTrigger>
      )
    }
    return <PixelText>{tracking || '--'}</PixelText>
  }

  const getTrackingID = (
    tracking_id: string,
    store_id: string,
    source_order_id: string,
  ) => {
    const order_ref = `${store_id}-${source_order_id}`
    if (order_ref === tracking_id) {
      return 'awaiting'
    }
    return tracking_id
  }

  const getWhatsappNumber = (phone: string) => {
    let phoneNumber = phone
    if (phone?.charAt(0) === '0') {
      phoneNumber = phone?.slice(1)
    }
    if (phone?.slice(0, 2) !== '92') {
      phoneNumber = `92${phoneNumber}`
    }
    return phoneNumber
  }

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return colors.warning
      case 2:
        return colors.pink
      case 3:
        return colors.success
      case 4:
        return colors.warning
      case 5:
        return colors.primary
      case 6:
        return colors.danger
      case 7:
        return colors.secondary
      case 8:
        return colors.purple
      default:
        return colors.secondary
    }
  }

  const getStatusBgColor = (status: number) => {
    switch (status) {
      case 1:
        return colors.warningLight
      case 2:
        return colors.infoLight
      case 3:
        return colors.successLight
      case 4:
        return colors.warningLight
      case 5:
        return colors.primaryLight
      case 6:
        return colors.dangerLight
      case 7:
        return colors.secondaryLight
      case 8:
        return colors.purpleLight
      default:
        return colors.grayLighter
    }
  }

  const columns = [
    {
      dataKey: 'number',
      name: 'Order',
      sortable: true,
      width: '11%',
      selector: (row: any) => (
        <PixelFlexBox width="100%" height="auto" alignItems="center" gap="8px">
          <div>
            {row.meta.order_source === 'ems' ? (
              row.is_split_child ? (
                <>
                  {row.split_source_order_id}
                  <SplitIcon size={12} color={colors.primary} />
                </>
              ) : row.exchanged_from_order_id ? (
                <DcFlexBox height="auto" width="auto">
                  {row.store_order_id}&nbsp;
                  <PixelTooltip
                    tooltipText={`This order was created as an exchange for Order #${row.exchanged_from_order_id}`}
                  >
                    <ExchangeIcon size={12} color={colors.primary} />
                  </PixelTooltip>
                </DcFlexBox>
              ) : (
                row['store_order_id']
              )
            ) : (
              row['source_order_id']
            )}
          </div>
        </PixelFlexBox>
      ),
    },
    {
      dataKey: 'status',
      name: 'Status',
      sortable: true,
      width: '9%',
      selector: (row: any) =>
        row.status ? (
          <DcTag
            textColor={getStatusColor(row.status)}
            customBackgroundColor={getStatusBgColor(
              row.status_classification_id,
            )}
          >
            {row.status}
          </DcTag>
        ) : (
          '--'
        ),
    },
    {
      dataKey: 'date_created',
      name: 'Date',
      sortable: true,
      width: '12%',
      selector: (row: any) => (
        <PixelDate
          className="textSmall"
          value={row?.date_created}
          format={'ll, HH:mm'}
        />
      ),
    },
    {
      dataKey: 'full_name',
      name: 'Customer',
      sortable: true,
      width: '12%',
      selector: (row: any) => {
        const customerName = `${row?.shipping?.first_name || ''} ${
          row?.shipping?.last_name || ''
        }`.trim()

        return (
          <CustomerCell data-tag="allowRowEvents">
            {(row['is_returning'] ||
              row['is_duplicate'] ||
              row['is_refuser']) && (
              <CustomerIcons>
                {row['is_returning'] && (
                  <HistoryIcon size={12} color={colors.primary} />
                )}
                {row['is_duplicate'] && (
                  <DIcon size={12} color={colors.primary} />
                )}
                {row['is_refuser'] && (
                  <TriangleExclamationIcon size={12} color={colors.danger} />
                )}
              </CustomerIcons>
            )}
            <CustomerName>
              <TruncatedCellText text={customerName || null} />
            </CustomerName>
          </CustomerCell>
        )
      },
    },
    {
      dataKey: 'city',
      name: 'City',
      sortable: true,
      width: '11%',
      selector: (row: any) => (
        <CityCell>
          <TruncatedCellText text={row?.shipping?.city || null} />
        </CityCell>
      ),
    },
    {
      dataKey: 'phone',
      name: 'Phone',
      sortable: true,
      width: '12%',
      selector: (row: any) => (
        <PixelFlexBox
          gap="5px"
          alignItems="center"
          style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}
        >
          <PixelText>{row.shipping.phone || '--'}</PixelText>{' '}
          <PixelTooltip style={{ width: 'auto' }} tooltipText="Go to whatsapp">
            <a
              href={`https://wa.me/${getWhatsappNumber(row.shipping.phone)}`}
              target="_blank"
              rel="noreferrer"
            >
              <WhatsAppIcon size={16} color={colors.success} />
            </a>
          </PixelTooltip>
          {row?.wa_is_read_flag === 1 ? (
            ''
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '20px',
                height: '20px',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  backgroundColor: colors.grayLighter,
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                }}
              ></div>
            </div>
          )}
        </PixelFlexBox>
      ),
    },
    {
      dataKey: 'delivery_risk',
      name: 'Probability',
      width: '9%',
      selector: (row: any) => <DeliveryRiskCell risk={row.delivery_risk} />,
    },
    {
      dataKey: 'total',
      name: <PixelText>Amount</PixelText>,
      width: '7%',
      sortable: true,
      selector: (row: any) =>
        (
          <PixelCurrency
            data-tag="allowRowEvents"
            hideSymbol={true}
            value={row.total}
          />
        ) || '0',
    },
    {
      dataKey: 'tracking_id',
      name: <PixelText>Tracking</PixelText>,
      sortable: true,
      width: '14%',
      selector: (row: any) => (
        <PixelFlexBox flexDirection={'column'}>
          {getTrackingObject(
            getTrackingID(
              row?.courier?.tracking_id,
              row?.store_id,
              row?.source_order_id,
            ),
            row?.last_comment,
          )}
          <PixelText variant="light">
            {row?.courier?.courier_name || '--'}
          </PixelText>
        </PixelFlexBox>
      ),
    },
  ]

  const fixedHeight = (() => {
    if (isMobile && selectedOrders.length > 0) return 'calc(100vh - 312px)'
    if (isMobile) return 'calc(100vh - 240px)'
    if (isVisible) return 'calc(100vh - 357px)'

    return 'calc(100vh - 327px)'
  })()

  return (
    <PixelTable
      pagination={true}
      columns={columns}
      progressPending={tableLoading}
      data={orders}
      paginationResetDefaultPage={resetPaginationToggle}
      paginationServer
      paginationTotalRows={totalRows}
      highlightOnHover={true}
      isSearchable={true}
      pointerOnHover={true}
      subHeaderComponent={!isMobile && subHeaderComponent}
      paginationRowsPerPageOptions={[50, 100, 150, 200, 250, 500]}
      onChangeRowsPerPage={handlePerRowsChange}
      paginationPerPage={filters.page_size || 50}
      paginationDefaultPage={Number(filters.page) + 1}
      onChangePage={handlePageChange}
      onRowClicked={onRowClicked}
      clearSelectedRows={toggledClearRows}
      selectableRows
      onSelectedRowsChange={handleChange}
      fixedHeader
      expandableRows
      expandableRowsComponent={ExpandedComponent}
      expandableRowsHideExpander={true}
      expandableRowExpanded={(row) => Boolean(row.customer_notes)}
      expandOnRowClicked={true}
      fixedHeaderScrollHeight={fixedHeight}
      customStyles={{
        subHeader: {
          style: {
            maxheight: isMobile ? '0px !important' : 'auto',
            minHeight: isMobile ? '0px !important' : 'auto',
            padding: '0px !important',
          },
        },
        // rows: {
        //   style: {
        //     '&:nth-of-type(odd)': {
        //       backgroundColor: colors.grayLighter,
        //     },
        //     '&.has-customer-note': {
        //       backgroundColor: colors.primaryLight,
        //       '&:hover': {
        //         backgroundColor: colors.primaryLight,
        //       },
        //     },
        //   },
        // },
      }}
      conditionalRowStyles={[
        {
          when: (row: any) => Boolean(row.customer_notes),
          style: {
            backgroundColor: colors.primaryLight,
            '&:hover': {
              backgroundColor: colors.primaryLight,
            },
          },
          classNames: ['has-customer-note'],
        },
      ]}
    />
  )
}

const CustomerCell = styled.div`
  /* Position against the RDT cell (position: relative), not the expanding
   * .rdt_TableCell__value wrapper, so long text cannot widen that wrapper
   * or paint into the next column. Match default cell padding (16px) so
   * values stay left-aligned with the column header. */
  position: absolute;
  top: 0;
  bottom: 0;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`

const CustomerIcons = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
`

const CustomerName = styled.div`
  flex: 1 1 0%;
  width: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-align: left;
`

const CityCell = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`

const TextClamp = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-align: left;
`

const EllipsisText = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`

const TooltipHitTarget = styled.span`
  position: absolute;
  inset: 0;
  display: block;
  cursor: default;
`

export default OrdersTable
